/**
 * Auth Client
 * Operator authentication and session management
 */

import type { Operator, OperatorRole, OperatorSession } from './types';
import { ROLE_PERMISSIONS } from './constants';
import {
  getCurrentOperator,
  storeOperatorSession,
  clearOperatorSession,
} from './base-client';

// =============================================================================
// Authentication
// =============================================================================

/**
 * Authenticate operator by ID
 * In production, this uses the backend API for validation.
 * In development with mock mode, uses local mock operators.
 *
 * @param operatorId - The operator ID to authenticate
 * @returns Authenticated operator or null if not found/invalid
 *
 * @example
 * ```ts
 * const operator = await authenticateOperator('test-admin-001');
 * if (operator) {
 *   console.log(`Logged in as ${operator.name}`);
 * }
 * ```
 */
export async function authenticateOperator(operatorId: string): Promise<Operator | null> {
  try {
    // Use local API route for database authentication
    const response = await fetch('/api/operator/auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ operatorId }),
    });

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    // API returns { success: true, operator: {...} } - extract the operator object
    const operator = data.operator || data;

    if (!operator || !operator.role) {
      console.error('Invalid operator response:', data);
      return null;
    }

    // Store operator session
    storeOperatorSession(
      operatorId,
      operator.role,
      operator.name,
      operator.businessIds || []
    );

    return operator;
  } catch (error) {
    console.error('Error authenticating operator:', error);
    return null;
  }
}

/**
 * Get current operator session
 * Returns the currently authenticated operator's session data.
 *
 * @returns Current operator session or null if not authenticated
 *
 * @example
 * ```ts
 * const session = getOperatorSession();
 * if (session) {
 *   console.log(`Current role: ${session.role}`);
 * }
 * ```
 */
export function getOperatorSession(): OperatorSession | null {
  return getCurrentOperator();
}

/**
 * Logout current operator
 * Clears all session data from storage.
 *
 * @example
 * ```ts
 * logoutOperator();
 * // User is now logged out
 * ```
 */
export function logoutOperator(): void {
  clearOperatorSession();
}

// =============================================================================
// Permission Checking
// =============================================================================

/**
 * Check if current operator has a specific permission
 *
 * @param permission - Permission key to check
 * @returns true if operator has the permission, false otherwise
 *
 * @example
 * ```ts
 * if (hasPermission('canViewConversations')) {
 *   // Show conversations
 * }
 * ```
 */
export function hasPermission(permission: keyof typeof ROLE_PERMISSIONS.ADMIN): boolean {
  const operator = getCurrentOperator();
  if (!operator) return false;

  return ROLE_PERMISSIONS[operator.role][permission];
}

/**
 * Check if current operator can view full PII
 *
 * @returns true if operator can view unmasked PII
 */
export function canViewFullPII(): boolean {
  return hasPermission('canViewFullPII');
}

/**
 * Check if current operator can manage other operators
 *
 * @returns true if operator can manage operators
 */
export function canManageOperators(): boolean {
  return hasPermission('canManageOperators');
}

/**
 * Check if current operator can export data
 *
 * @returns true if operator can export data
 */
export function canExportData(): boolean {
  return hasPermission('canExportData');
}

/**
 * Check if current operator can process LGPD requests
 *
 * @returns true if operator can process LGPD requests
 */
export function canProcessLGPD(): boolean {
  return hasPermission('canProcessLGPD');
}

/**
 * Get all permissions for current operator
 *
 * @returns Permission object or null if not authenticated
 */
export function getOperatorPermissions(): typeof ROLE_PERMISSIONS.ADMIN | null {
  const operator = getCurrentOperator();
  if (!operator) return null;

  return ROLE_PERMISSIONS[operator.role];
}

/**
 * Check if operator has one of the specified roles
 *
 * @param roles - Array of roles to check against
 * @returns true if operator has one of the roles
 */
export function hasRole(...roles: OperatorRole[]): boolean {
  const operator = getCurrentOperator();
  if (!operator) return false;

  return roles.includes(operator.role);
}

// Re-export for convenience
export { ROLE_PERMISSIONS } from './constants';
